'use client'

import React, {
  createContext,
  createRef,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Stage } from 'konva/lib/Stage'
import { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch'
import { useParams } from 'next/navigation'

import { calculateAspectRatioFit } from '@/components/organisms/Blueprint/utils'
import {
  Blueprint,
  DrawingMode,
  Point,
  Section,
  Shape,
  Transform,
} from '@/types'
import { useBlueprint } from '@/services/queries'
import { useProject } from '@/services/queries/projects'
import {
  selectPrimaryProjectBlueprint,
  useProjectBlueprints,
} from '@/services/queries/blueprint'
import { ShapeParams, useUpsertShape } from '@/services/mutations'
import { useSection } from '@/services/queries/section'

interface BlueprintContextType {
  // Stage and Transform
  transformComponentRef?: React.RefObject<ReactZoomPanPinchRef | null>
  parentRef?: React.RefObject<HTMLDivElement | null>
  stageRef?: React.RefObject<Stage | null>
  sectionImage: { width: number; height: number } | null
  setSectionImage: React.Dispatch<
    React.SetStateAction<{ width: number; height: number } | null>
  >
  imageDimensions: { width: number; height: number }
  stageToImageCoordinates: (imageX: number, imageY: number) => Point
  imageToStageCoordinates: (imageX: number, imageY: number) => Point
  getTransform: () => Transform
  imagePosition: Point
  stageSize: { width: number; height: number }

  // Tools
  drawingMode: DrawingMode
  setDrawingMode: React.Dispatch<React.SetStateAction<DrawingMode>>
  selectedColor: string
  setSelectedColor: React.Dispatch<React.SetStateAction<string>>
  isDrawing: boolean
  setIsDrawing: React.Dispatch<React.SetStateAction<boolean>>
  scale: number
  setScale: React.Dispatch<React.SetStateAction<number>>

  // Zoom
  pixelsPerMeter: number
  setPixelsPerMeter: (pixelsPerMeter: number) => void
  zoomLevel: number
  setZoomLevel: (zoomLevel: number) => void
  effectivePixelsPerMeter: number

  // Blueprint data
  blueprint: Blueprint | undefined
  sections: Section[]
  shapes: Shape[]
  setShapes: (shape: ShapeParams) => void

  // Create and edit shapes
  currentShape: Shape | null
  setCurrentShape: React.Dispatch<React.SetStateAction<Shape | null>>

  // Selection
  hoveredShape: Shape | null
  setHoveredShape: React.Dispatch<React.SetStateAction<Shape | null>>
  selectedSection: Section | null
  setSelectedSection: React.Dispatch<React.SetStateAction<Section | null>>
  selectedShape: Shape | null
  setSelectedShape: React.Dispatch<React.SetStateAction<Shape | null>>
  selectedMarkerId: string | null
  setSelectedMarkerId: React.Dispatch<React.SetStateAction<string | null>>

  isLoading: boolean
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  resetToDefault: () => void
}

const BlueprintContext = createContext<BlueprintContextType | undefined>(
  undefined,
)

const parentRef = createRef<HTMLDivElement>()
const stageRef = createRef<Stage>()
const transformComponentRef = createRef<ReactZoomPanPinchRef>()

export function BlueprintProvider({ children }: { children: ReactNode }) {
  const [sectionImage, setSectionImage] = useState<{
    width: number
    height: number
  } | null>(null)

  const [selectedSection, setSelectedSection] = useState<Section | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const [currentShape, setCurrentShape] = useState<Shape | null>(null)
  const [drawingMode, setDrawingMode] = useState<DrawingMode>('move')
  const [selectedColor, _setSelectedColor] = useState('#0000FF')
  const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [scale, setScale] = useState(1)
  const [selectedShape, _setSelectedShape] = useState<Shape | null>(null)
  const [hoveredShape, setHoveredShape] = useState<Shape | null>(null)

  const [stageSize, setStageSize] = useState({ width: 0, height: 0 })

  const [pixelsPerMeter, setPixelsPerMeter] = useState<number>(100) // Par défaut, 100 pixels = 1 mètre
  const [zoomLevel, setZoomLevel] = useState<number>(1)

  // Retrieve project and blueprint data
  const { id: projectID } = useParams<{ id: string }>()
  const { data: project } = useProject(projectID)
  const { data: projectBlueprints, isSuccess: projectBlueprintsLoaded } =
    useProjectBlueprints(projectID)
  const blueprintID = useMemo(() => {
    if (projectBlueprintsLoaded) {
      return selectPrimaryProjectBlueprint(projectBlueprints)?.id
    }

    return selectPrimaryProjectBlueprint(project?.blueprints)?.id
  }, [project?.blueprints, projectBlueprints, projectBlueprintsLoaded])

  const { data: blueprint, isSuccess } = useBlueprint(blueprintID)
  const sections = useMemo(() => blueprint?.sections?.items ?? [], [blueprint])

  const selectedSectionIsCurrent = useMemo(
    () =>
      Boolean(
        selectedSection?.id &&
        sections.some((section) => section.id === selectedSection.id),
      ),
    [sections, selectedSection?.id],
  )
  const selectedSectionID = selectedSectionIsCurrent
    ? selectedSection?.id
    : undefined

  useEffect(() => {
    if (!isSuccess) return

    if (sections.length === 0) {
      if (selectedSection) setSelectedSection(null)
      return
    }

    const currentSection = selectedSection?.id
      ? sections.find((section) => section.id === selectedSection.id)
      : undefined

    if (currentSection) {
      if (currentSection !== selectedSection) setSelectedSection(currentSection)
      return
    }

    setSelectedSection(sections[0])
  }, [isSuccess, sections, selectedSection])

  const { data: sectionData } = useSection(selectedSectionID)
  const shapes = useMemo(() => sectionData?.shapes?.items ?? [], [sectionData])

  const { mutate: setShapes } = useUpsertShape()

  // Handle Canvas
  const updateStageSize = useCallback(() => {
    if (parentRef.current) {
      const { width, height } = parentRef.current.getBoundingClientRect()
      setStageSize({ width, height })
    }
  }, [])

  useEffect(() => {
    updateStageSize() // Initial size update

    const resizeObserver = new ResizeObserver(updateStageSize)
    if (parentRef.current) {
      resizeObserver.observe(parentRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [updateStageSize])

  const imageDimensions = useMemo(
    () =>
      !sectionImage
        ? { width: 800, height: 600 }
        : calculateAspectRatioFit(
            sectionImage.width,
            sectionImage.height,
            stageSize.width,
            stageSize.height,
          ),
    [sectionImage, stageSize],
  )

  const imagePosition = useMemo(
    () => ({
      x: (stageSize.width - imageDimensions.width) / 2,
      y: (stageSize.height - imageDimensions.height) / 2,
    }),
    [stageSize, imageDimensions],
  )

  const getTransform = useCallback((): Transform => {
    const transform = transformComponentRef.current?.state
    return {
      scale: transform?.scale || 1,
      x: transform?.positionX || 0,
      y: transform?.positionY || 0,
    }
  }, [])

  const stageToImageCoordinates = useCallback(
    (stageX: number, stageY: number): Point => {
      const transform = getTransform()
      const x = (stageX - imagePosition.x - transform.x) / transform.scale
      const y = (stageY - imagePosition.y - transform.y) / transform.scale
      return {
        x: (x / imageDimensions.width) * (sectionImage?.width || 0),
        y: (y / imageDimensions.height) * (sectionImage?.height || 0),
      }
    },
    [imagePosition, imageDimensions, sectionImage, getTransform],
  )

  const imageToStageCoordinates = useCallback(
    (imageX: number, imageY: number): Point => {
      const transform = getTransform()
      const x = (imageX / (sectionImage?.width || 1)) * imageDimensions.width
      const y = (imageY / (sectionImage?.height || 1)) * imageDimensions.height
      return {
        x: x * transform.scale + imagePosition.x + transform.x,
        y: y * transform.scale + imagePosition.y + transform.y,
      }
    },
    [imagePosition, imageDimensions, sectionImage, getTransform],
  )

  const setSelectedShape = useCallback(
    (shape: Shape | null) => {
      _setSelectedShape(shape)
      if (shape && shape.color) {
        _setSelectedColor(shape.color)
      }
    },
    [_setSelectedShape, _setSelectedColor],
  )

  const setSelectedColor = useCallback(
    (color: string) => {
      _setSelectedColor(color)
      if (selectedShape) {
        setShapes({
          upsertType: 'update',
          input: { ...selectedShape, color },
        })
        _setSelectedShape({ ...selectedShape, color })
      }
    },
    [selectedShape, setShapes],
  )

  const effectivePixelsPerMeter = useMemo(() => {
    return pixelsPerMeter * zoomLevel
  }, [pixelsPerMeter, zoomLevel])

  const resetToDefault = useCallback(() => {
    setDrawingMode('move')
    setIsDrawing(false)
    setCurrentShape(null)
    setHoveredShape(null)
    setSelectedShape(null)
  }, [setSelectedShape])

  const value = React.useMemo(
    () => ({
      transformComponentRef,
      parentRef,
      stageRef,
      shapes,
      setShapes,
      currentShape,
      setCurrentShape,
      drawingMode,
      setDrawingMode,
      selectedColor,
      setSelectedColor,
      isDrawing,
      setIsDrawing,
      scale,
      setScale,
      imageDimensions,
      stageToImageCoordinates,
      imageToStageCoordinates,
      getTransform,
      imagePosition,
      stageSize,
      sectionImage,
      setSectionImage,

      blueprint,
      sections,

      hoveredShape,
      setHoveredShape,
      selectedSection,
      setSelectedSection,
      isLoading,
      setIsLoading,
      selectedShape,
      setSelectedShape,
      selectedMarkerId,
      setSelectedMarkerId,
      pixelsPerMeter,
      setPixelsPerMeter,
      zoomLevel,
      setZoomLevel,
      effectivePixelsPerMeter,
      resetToDefault,
    }),
    [
      shapes,
      setShapes,
      currentShape,
      setCurrentShape,
      drawingMode,
      setDrawingMode,
      selectedColor,
      setSelectedColor,
      isDrawing,
      setIsDrawing,
      scale,
      setScale,
      imageDimensions,
      stageToImageCoordinates,
      imageToStageCoordinates,
      getTransform,
      imagePosition,
      stageSize,
      sectionImage,
      setSectionImage,
      blueprint,
      sections,
      hoveredShape,
      setHoveredShape,
      selectedSection,
      setSelectedSection,
      isLoading,
      setIsLoading,
      selectedShape,
      setSelectedShape,
      selectedMarkerId,
      setSelectedMarkerId,
      pixelsPerMeter,
      setPixelsPerMeter,
      zoomLevel,
      setZoomLevel,
      effectivePixelsPerMeter,
      resetToDefault,
    ],
  )

  return (
    <BlueprintContext.Provider value={value}>
      {children}
    </BlueprintContext.Provider>
  )
}

export const useBlueprintContext = () => {
  const context = useContext(BlueprintContext)
  if (context === undefined) {
    throw new Error(
      'useBlueprintContext must be used within a BlueprintProvider',
    )
  }
  return context
}
